import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { paymentErrorMessage } from '../paymentErrors';
import './pendingInvoicePaymentModal.css';

// Dedicated "Pending Invoice Payment" bank-transfer flow (2026-07-17:
// "we can't just use the same as for add funds... should be fixed amount").
// NOT the Add Funds form: the amount is FIXED to the client's fresh pending-
// invoices total (big read-only display, never an input), no bonus, no card.
// Submits the standard multipart deposit POST with purpose='settle_pending' —
// the server re-validates the amount against the fresh total and applies it
// to the invoices once an admin confirms the transfer.
// entityBreakdown ([{label, amount}] | null) — per-billing-entity split of the
// fixed total, multi-billing clients only. Display-only rows above the total;
// null/absent (every normal client) renders exactly as before.
// INLINE (2026-08-14): on the invoice payment PAGE, bank transfer is one of
// the methods you pick at the top — not a modal that opens on top of the page
// you are already paying on. `inline` renders the same body with no overlay and
// no modal chrome; `onClose` becomes "go back to the method choice". The modal
// form is unchanged for any caller that still wants it.
//
// WALLET + TRANSFER (2026-08-14): `walletPortion` is the part of the debt
// the client pays from their prepaid cash, so `total` here is what they must
// actually TRANSFER (debt − wallet). The server re-clamps the wallet share
// against the live balance and refuses any transfer that isn't exactly the
// remainder — this figure is display, never the authority.
export default function PendingInvoicePaymentModal({
    total, onClose, onSuccess, entityBreakdown = null, inline = false,
    walletPortion = 0, debtTotal = null,
    // Separate entity wallets: the pool (wallet) this transfer settles.
    walletClientId = null,
}) {
    // Seeded from the breakdown at open; ADOPTS the server's fresh figure if
    // the exact-amount check rejects a stale total (invoices changed while the
    // modal sat open) — the client sees the new amount without reopening.
    const [fixedTotal, setFixedTotal] = useState(Number(total || 0));
    const [amountChangedNote, setAmountChangedNote] = useState('');

    const [bankReference, setBankReference] = useState('');
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) setFile(acceptedFiles[0]);
    }, []);

    // Rejected drops (oversize / wrong type) must SAY so. Without this the file
    // is silently discarded: no chip, no message, and the Submit button stays
    // disabled — the flow looks broken exactly when the client is trying to
    // prove they paid. (Same handler as ClientDepositForm.)
    const onDropRejected = useCallback((fileRejections) => {
        const code = fileRejections?.[0]?.errors?.[0]?.code;
        if (code === 'file-too-large') {
            toast.error('That file is over 10MB — please upload a smaller image or PDF.');
        } else if (code === 'file-invalid-type') {
            toast.error('That file type isn’t supported — please upload a JPEG, PNG, WebP or PDF.');
        } else {
            toast.error('That file couldn’t be added — please upload one JPEG, PNG, WebP or PDF up to 10MB.');
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
            'application/pdf': ['.pdf']
        },
        maxSize: 10 * 1024 * 1024,
        maxFiles: 1
    });

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy');
        });
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error('Please upload proof of payment');
            return;
        }
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('amount', fixedTotal);
            formData.append('payment_method', 'bank_transfer');
            formData.append('bank_reference', bankReference);
            formData.append('purpose', 'settle_pending');
            if (walletClientId) formData.append('wallet_client_id', String(walletClientId));
            // Intent, not an amount — the server clamps it to the live cash
            // balance and derives the transfer figure from its own fresh total.
            if (walletPortion > 0.009) formData.append('wallet_portion', walletPortion);
            formData.append('proof_of_payment', file);

            const res = await axios.post('/api/client-requests/wallet/deposits', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSubmitted(true);
            if (onSuccess) onSuccess(res.data);
        } catch (err) {
            // Exact-amount check rejected a stale figure — the server answers
            // with the fresh figure to transfer. On a split that is
            // transfer_amount (debt − wallet), never the gross debt.
            const fresh = Number(err.response?.data?.transfer_amount
                ?? err.response?.data?.breakdown?.total
                ?? err.response?.data?.total);
            if (err.response?.status === 400 && Number.isFinite(fresh) && fresh > 0
                && Math.abs(fresh - fixedTotal) > 0.009) {
                setFixedTotal(fresh);
                setAmountChangedNote(`Your pending amount changed to ${money(fresh)} — please transfer this amount.`);
            } else {
                console.error('Error submitting pending invoice payment:', err);
                toast.error(paymentErrorMessage(err, 'Failed to submit the payment'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const body = (
        <>
                {!inline && (
                    <>
                        <button type="button" className="cpip__x" onClick={onClose} disabled={submitting}>&times;</button>
                        <h3 className="cpip__title">Pending Invoice Payment</h3>
                    </>
                )}

                {submitted ? (
                    <>
                        <div className="cpip__success">
                            Transfer submitted — it will be applied to your pending invoices once our team confirms it.
                        </div>
                        <div className="cpip__footer">
                            <button type="button" className="cpip__btn cpip__btn--primary" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Fixed, read-only amount — the exact pending-invoices
                            total. NOT rendered inline: the page hosting this
                            already states the figure (and the per-profile split)
                            immediately above, and the bank block below repeats
                            it again — three copies of one number read as a
                            mistake (2026-08-14). */}
                        {!inline && (
                        <div className="cpip__amountBox">
                            <div className="cpip__amountLabel">Amount to transfer</div>
                            {/* Per-entity split (multi-billing clients only — null
                                for everyone else, so nothing extra renders). */}
                            {Array.isArray(entityBreakdown) && entityBreakdown.length > 0 && (
                                <div className="cpip__entities">
                                    {entityBreakdown.map((e, i) => (
                                        <div key={i} className="cpip__entityRow">
                                            <span>{e.label}</span>
                                            <span>{money(e.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="cpip__amountValue">{money(fixedTotal)}</div>
                            <div className="cpip__amountHint">
                                {walletPortion > 0.009 && debtTotal != null
                                    ? <>{money(debtTotal)} outstanding, less {money(walletPortion)} from your wallet balance.</>
                                    : 'Your outstanding invoices total.'}
                            </div>
                        </div>
                        )}

                        {amountChangedNote && (
                            <div className="cpip__amountChanged">{amountChangedNote}</div>
                        )}

                        {/* Company bank details (same details as the Add Funds bank step). */}
                        <div className="cpip__bank">
                            <div className="cpip__bankTitle">Transfer <strong>{money(fixedTotal)}</strong> to the following account:</div>
                            <div className="cpip__bankRow">
                                <span className="cpip__bankLabel">Company</span>
                                <span className="cpip__bankValue">Holiday Home Cleaning Services CO. L.L.C</span>
                            </div>
                            <div className="cpip__bankRow">
                                <span className="cpip__bankLabel">Bank</span>
                                <span className="cpip__bankValue">Mashreq Bank PSC</span>
                            </div>
                            <div className="cpip__bankRow">
                                <span className="cpip__bankLabel">IBAN</span>
                                <span className="cpip__bankValue">
                                    AE500330000019101412012
                                    <button type="button" className="cpip__copyBtn" onClick={() => copyToClipboard('AE500330000019101412012')}>
                                        Copy
                                    </button>
                                </span>
                            </div>
                            <div className="cpip__bankRow">
                                <span className="cpip__bankLabel">Account No.</span>
                                <span className="cpip__bankValue">
                                    019101412012
                                    <button type="button" className="cpip__copyBtn" onClick={() => copyToClipboard('019101412012')}>
                                        Copy
                                    </button>
                                </span>
                            </div>
                            <div className="cpip__bankRow">
                                <span className="cpip__bankLabel">SWIFT Code</span>
                                <span className="cpip__bankValue">BOMLAEAD</span>
                            </div>
                        </div>

                        <div className="cpip__field">
                            <label className="cpip__fieldLabel">Bank Reference (optional)</label>
                            <input
                                type="text"
                                className="cpip__input"
                                placeholder="Enter bank transfer reference"
                                value={bankReference}
                                onChange={(e) => setBankReference(e.target.value)}
                            />
                        </div>

                        <div className="cpip__field">
                            <label className="cpip__fieldLabel">Upload Proof of Payment *</label>
                            {!file ? (
                                <div {...getRootProps()} className={`cpip__dropzone ${isDragActive ? 'is-active' : ''}`}>
                                    <input {...getInputProps()} />
                                    <div className="cpip__dropzoneText">
                                        {isDragActive ? 'Drop the file here...' : 'Click or drag to upload proof of payment'}
                                    </div>
                                    <div className="cpip__dropzoneHint">JPEG, PNG, WebP, or PDF (max 10MB)</div>
                                </div>
                            ) : (
                                <div className="cpip__filePreview">
                                    <span className="cpip__fileName">{file.name}</span>
                                    <button type="button" className="cpip__fileRemove" onClick={() => setFile(null)}>Remove</button>
                                </div>
                            )}
                        </div>

                        <div className="cpip__hint">
                            Once submitted, the payment is applied to your pending invoices as soon as our team confirms the transfer.
                        </div>

                        <div className="cpip__footer">
                            <button type="button" className="cpip__btn cpip__btn--ghost" onClick={onClose} disabled={submitting}>
                                {inline ? 'Back' : 'Cancel'}
                            </button>
                            <button
                                type="button"
                                className="cpip__btn cpip__btn--primary"
                                disabled={!file || submitting}
                                onClick={handleSubmit}
                            >
                                {submitting ? 'Submitting…' : `Submit ${money(fixedTotal)} Payment`}
                            </button>
                        </div>
                    </>
                )}
        </>
    );

    if (inline) return <div className="cpip cpip--inline">{body}</div>;

    return (
        <div className="cpip__overlay" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
            <div className="cpip__modal">{body}</div>
        </div>
    );
}
